import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import logo from "../assets/logo.png";
import API_BASE_URL from "../config";

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Load Bootstrap dynamically
  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  // Fetch roles
  useEffect(() => {
  const token = sessionStorage.getItem("token");

  fetch(`${API_BASE_URL}/api/roles/all`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
    .then((res) => {
      if (!res.ok) throw new Error("Failed to fetch roles");
      return res.json();
    })
    .then((data) => {
      const roleNames = data.map((r) => r.name);
      setRoles(roleNames);

      const defaultRole = roleNames.includes("User")
        ? "User"
        : roleNames[0] || "";

      setFormData((prev) => ({ ...prev, role: defaultRole }));
    })
    .catch(() => {
      setErrorMessage("Unable to load roles from server");
    });
}, []);


  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.password.trim()
    ) {
      setErrorMessage("Please fill in all fields.");
      return;
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      roles: [formData.role],
    };

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (response.ok) {
        alert("Registration successful!");
        navigate("/");
      } else {
        setErrorMessage(
          data.message || `Error ${response.status}: Registration failed`
        );
      }
    } catch (err) {
      setErrorMessage("Something went wrong while registering.");
    } finally {
      setLoading(false);
    }
  };

  const bgImage =
    "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=1600";

  return (
    <div
      className=" p-6 d-flex align-items-center justify-content-center vh-100"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        position: "relative",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      {/* Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0, 0, 0, 0.55)",
          zIndex: 0,
        }}
      ></div>

      {/* Card */}
      <div
        className="row g-0 rounded-4 shadow-lg overflow-hidden"
        style={{
          width: "90%",
          maxWidth: "850px",
          zIndex: 1,
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(15px)",
          border: "1px solid rgba(255,255,255,0.2)",
          animation: "fadeIn 1s ease-in-out",
        }}
      >
        {/* Left Column */}
        <div
          className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white p-4 p-md-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(3, 24, 46, 0.7), rgba(69, 2, 177, 0.7))",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            width="190"
            className="mb-4"
            style={{ filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.3))" }}
          />
          <h3 className="fw-bold text-center mb-3">Welcome to BALINTRA</h3>
          <p className="text-center opacity-75">
            Empower your team and streamline your workflow with ease.
          </p>
        </div>

        {/* Right Column */}
        <div className="col-md-6 bg-transparent text-white p-4 p-md-5">
          <h4
            className="fw-bold mb-4 text-center"
            style={{
              background: "linear-gradient(90deg, #f6f7f8ff, #6610f2)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Create Account
          </h4>

          {errorMessage && (
            <div
              className="alert alert-danger text-center py-2"
              style={{ fontSize: "0.9rem" }}
            >
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label fw-semibold text-light">Name</label>
              <input
                type="text"
                name="name"
                className="form-control bg-transparent text-light border-light"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{ borderRadius: "8px", padding: "10px" }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-light">Email</label>
              <input
                type="email"
                name="email"
                className="form-control bg-transparent text-light border-light"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ borderRadius: "8px", padding: "10px" }}
              />
            </div>

            <div className="mb-3 position-relative">
              <label className="form-label fw-semibold text-light">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control bg-transparent text-light border-light"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                required
                style={{ borderRadius: "8px", padding: "10px" }}
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  top: "38px",
                  right: "12px",
                  cursor: "pointer",
                  color: "#d1d1d1",
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold text-light">Role</label>
              <select
                className="form-select bg-transparent text-light border-light"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                style={{ borderRadius: "8px", padding: "10px" }}
              >
                {/* {roles.length > 0 ? (
                  roles.map((roleName, index) => (
                    <option key={index} value={roleName}>
                      {roleName}
                    </option>
                  ))
                ) : ( */}
                  <option>Admin</option>
                {/* )} */}
              </select>
            </div>

            <div className="d-grid mb-3">
              <button
                type="submit"
                className="btn fw-semibold"
                disabled={loading}
                style={{
                  borderRadius: "8px",
                  background: "linear-gradient(90deg, #007bff, #6610f2)",
                  color: "white",
                  border: "none",
                  transition: "transform 0.2s ease",
                }}
                onMouseEnter={(e) => (e.target.style.transform = "scale(1.03)")}
                onMouseLeave={(e) => (e.target.style.transform = "scale(1.0)")}
              >
                {loading ? "Registering..." : "Register"}
              </button>
            </div>

            <div className="text-center">
              <p className="mb-0 text-light">
                Already have an account?{" "}
                <span
                  onClick={() => navigate("/")}
                  style={{
                    color: "#0d6efd",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Login
                </span>
              </p>
            </div>
          </form>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default RegisterPage;
